'use client'

import { ReactNode, useMemo, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import {
  ColumnDef,
  createColumnHelper,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { usePrevious } from '@uidotdev/usehooks'
import { isNil } from 'lodash'
import {
  CopyIcon,
  ExternalLinkIcon,
  PencilIcon,
  SproutIcon,
  TriangleAlertIcon,
  Undo2Icon,
} from 'lucide-react'

import { EditLotDialog } from '~/app/accounting/EditLotDialog'
import { LotDetailDialog } from '~/app/accounting/LotDetailDialog'
import { LotRecomputeBanner } from '~/app/accounting/LotRecomputeBanner'
import { LotTrustSignalsBanner } from '~/app/accounting/LotTrustSignalsBanner'
import { LotTrustSignalsDialog } from '~/app/accounting/LotTrustSignalsDialog'
import { RevokeLotEditDialog } from '~/app/accounting/RevokeLotEditDialog'
import { SeedLotDialog } from '~/app/accounting/SeedLotDialog'
import { EMPTY_VALUE } from '~/app/accounting/display-utils'
import { CostBasisFilterValue } from '~/app/accounting/filters/CostBasisFilterButton'
import { RemainingFilterValue } from '~/app/accounting/filters/RemainingFilterButton'
import { UnitCostFilterValue } from '~/app/accounting/filters/UnitCostFilterButton'
import { buildLotRows, LotRow, OrgLot } from '~/app/accounting/lot-rows'
import { LotTrustSignals } from '~/app/accounting/lot-trust-signals'
import { Period } from '~/app/accounting/period-utils'
import { LotRecomputeState } from '~/app/accounting/useLotRecompute'
import { Table, TablePageSize } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import {
  CurrencyAmount,
  TokenDisplay,
  Tooltip,
  tokenBalanceFormatters,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { Dropdown } from '~/components/component-library/Dropdown'
import { Timestamp } from '~/components/component-library/Timestamp'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress } from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { formatWithLocalTimezone } from '~/utils/time'

const INITIAL_PAGE_SIZE: TablePageSize = 25

// Raw slugs from the lot engine; anything unrecognized renders as its slug.
const BASIS_SOURCE_LABELS: Record<string, string> = {
  indexed_price: 'Indexed price',
  carried: 'Carried',
  synthetic_shortfall: 'Synthetic (shortfall)',
  synthetic_accrual: 'Synthetic (accrual)',
  seeded: 'Seeded',
  asserted: 'Edited',
}

const WARNING_LABELS: Record<string, string> = {
  insufficient_lots: 'Sold more than tracked lots held',
  unresolved_bridge_carry: 'Bridge source lot unresolved',
  unpaired_internal_leg: 'Unmatched internal transfer',
  unpriced_transfer: 'No price at transfer time',
}

const columnHelper = createColumnHelper<LotRow>()

const formatTokenAmount = (row: LotRow, amount: number) =>
  tokenBalanceFormatters.formatAbbreviatedTokenAmount(amount, {
    address: row.tokenAddress as IAddress,
    chainId: row.chainId,
  })

// The badge shown while a write awaits the recompute that lands it.
const pendingBadge = (row: LotRow) => {
  if (row.pendingEdit)
    return {
      label: 'Edited · pending',
      tooltip:
        'This edit is pending and will be reflected across your books soon',
    }
  if (row.pendingRevert)
    return {
      label: 'Revoke · pending',
      tooltip:
        'This revoke is pending and the lot will return to its original values soon',
    }
  return null
}

const LotPriceCell = ({ row }: { row: LotRow }) => {
  const pending = pendingBadge(row)
  return (
    <div className="flex flex-col items-end">
      <span className="flex items-center gap-1">
        {row.warnings.length > 0 && (
          <Tooltip
            className="relative inline-flex"
            content={row.warnings
              .map((warning) => WARNING_LABELS[warning] ?? warning)
              .join('; ')}
          >
            <TriangleAlertIcon className="text-amber-500 h-3.5 w-3.5" />
          </Tooltip>
        )}
        <CurrencyAmount layout="standard" size="small" value={row.unitPrice} />
      </span>
      {pending ? (
        <Tooltip className="relative inline-flex" content={pending.tooltip}>
          <span className="text-xs text-secondary">{pending.label}</span>
        </Tooltip>
      ) : (
        row.basisSource !== 'indexed_price' && (
          <span className="text-xs text-secondary">
            {BASIS_SOURCE_LABELS[row.basisSource] ?? row.basisSource}
          </span>
        )
      )}
    </div>
  )
}

// The bar is fixed-width so bars align across rows; the text truncates,
// never the bar.
const RemainingCell = ({ row }: { row: LotRow }) => {
  const fraction = row.quantity > 0 ? row.quantityRemaining / row.quantity : 0
  return (
    <div className="flex w-full min-w-0 items-center gap-3">
      <Tooltip
        className="relative shrink-0"
        content={`${Math.round(fraction * 100)}% remaining`}
      >
        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
          <div
            className="h-full rounded-full bg-green-600"
            style={{ width: `${Math.min(100, Math.max(0, fraction * 100))}%` }}
          />
        </div>
      </Tooltip>
      <span className="min-w-0 truncate whitespace-nowrap text-secondary">
        {formatTokenAmount(row, row.quantityRemaining)}/
        {formatTokenAmount(row, row.quantity)} {row.symbol}
      </span>
    </div>
  )
}

// Basis sources whose lots open from an inbound transfer, so an edit's
// anchor can match. Synthetic and seeded lots have no anchor.
const EDITABLE_BASIS_SOURCES = new Set(['indexed_price', 'carried', 'asserted'])

const ActionsCell = ({ row }: { row: LotRow }) => {
  const { setGlobalModalState } = useAuth()
  const explorerPath = row.sourceTransactionHash
    ? getExplorerTransactionLinkChainId(row.sourceTransactionHash, row.chainId)
    : undefined
  const openDialog = (content: ReactNode) =>
    setGlobalModalState({ isOpen: true, content })
  const canEdit =
    row.sourceTransferId !== null && EDITABLE_BASIS_SOURCES.has(row.basisSource)
  // Seeding designates the lot's source transfer, so it needs one, and only
  // an indexed-price open can be re-explained as arriving pre-custody
  // inventory — carried and seeded lots already have their basis story.
  const canSeed =
    row.sourceTransferId !== null && row.basisSource === 'indexed_price'

  return (
    <div className="flex justify-end">
      <Dropdown
        align="end"
        side="bottom"
        items={[
          ...(canEdit
            ? [
                {
                  content: 'Edit lot',
                  icon: <PencilIcon />,
                  onClick: () => openDialog(<EditLotDialog lot={row} />),
                },
              ]
            : []),
          ...(canSeed
            ? [
                {
                  content: 'Seed lot',
                  icon: <SproutIcon />,
                  onClick: () => openDialog(<SeedLotDialog lot={row} />),
                },
              ]
            : []),
          ...((row.basisSource === 'asserted' || row.pendingEdit) &&
          !row.pendingRevert
            ? [
                {
                  content: 'Revoke edits',
                  icon: <Undo2Icon />,
                  onClick: () => openDialog(<RevokeLotEditDialog lot={row} />),
                },
              ]
            : []),
          {
            content: 'Copy lot ID',
            icon: <CopyIcon />,
            onClick: () => copyToClipboard(row.id, 'Lot ID copied'),
          },
          ...(explorerPath
            ? [
                {
                  content: 'Open source transaction in explorer',
                  icon: <ExternalLinkIcon />,
                  href: { path: explorerPath, newTab: true },
                },
              ]
            : []),
        ]}
      >
        <TableActionTrigger aria-label="Lot actions" />
      </Dropdown>
    </div>
  )
}

export const LotsTab = ({
  period,
  accountIds,
  tokenKeys,
  search,
  remaining,
  costBasis,
  unitCost,
  recompute,
}: {
  period: Period | null
  accountIds?: string[]
  tokenKeys?: string[]
  search?: string
  remaining: RemainingFilterValue
  costBasis: CostBasisFilterValue
  unitCost: UnitCostFilterValue
  recompute: LotRecomputeState
}) => {
  const { serverGetRequest } = useServerRequests()
  const { org } = useActiveOrg()
  const { setGlobalModalState } = useAuth()
  const [tz] = useAccountingTimezone()

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PAGE_SIZE,
  })

  // Lots are "open as of the period end"; an unbounded period means as of now,
  // which the server defaults when the param is omitted. The period start
  // bounds the acquisition date so the table answers to the date filter.
  const openAsOf = period?.endDate?.toISOString()
  const startDate = period?.startDate?.toISOString()
  const trimmedSearch = search?.trim() || undefined
  // Whole-token values, passed through as typed: the server scales by the
  // token's decimals.
  const minRemaining = remaining.min.trim() || undefined
  const maxRemaining = remaining.max.trim() || undefined
  const status = remaining.status
  const minCostBasis = costBasis.min.trim() || undefined
  const maxCostBasis = costBasis.max.trim() || undefined
  const minUnitCost = unitCost.min.trim() || undefined
  const maxUnitCost = unitCost.max.trim() || undefined

  // Sorted server-side; the sortable column ids match the API's sortBy
  // values. Empty means the default chronological order.
  const [sorting, setSorting] = useState<SortingState>([])
  const sortBy = sorting[0]?.id
  const sortDirection = sorting[0]?.desc ? 'desc' : 'asc'

  // Reset to first page when the scope changes (derived state during render).
  const filterKey = JSON.stringify([
    tokenKeys,
    openAsOf,
    startDate,
    accountIds,
    trimmedSearch,
    minRemaining,
    maxRemaining,
    status,
    minCostBasis,
    maxCostBasis,
    minUnitCost,
    maxUnitCost,
    sortBy,
    sortDirection,
  ])
  const prevFilterKeyRef = useRef(filterKey)
  if (prevFilterKeyRef.current !== filterKey) {
    prevFilterKeyRef.current = filterKey
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEYS.ORG_ACCOUNTING_LOTS,
      org?.id,
      tokenKeys,
      openAsOf,
      startDate,
      accountIds,
      trimmedSearch,
      minRemaining,
      maxRemaining,
      status,
      minCostBasis,
      maxCostBasis,
      minUnitCost,
      maxUnitCost,
      sortBy,
      sortDirection,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    queryFn: async () => {
      if (!org?.id) throw new Error('Org ID required')

      const response = await serverGetRequest<{
        items: OrgLot[]
        totalCount: number
        trustSignals: LotTrustSignals
      }>({
        path: '/api/org/accounting/lots',
        params: {
          orgId: org.id,
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          ...(tokenKeys?.length ? { tokens: tokenKeys.join(',') } : {}),
          ...(openAsOf ? { openAsOf } : {}),
          ...(startDate ? { startDate } : {}),
          ...(accountIds?.length ? { accountIds: accountIds.join(',') } : {}),
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
          ...(minRemaining ? { minRemaining } : {}),
          ...(maxRemaining ? { maxRemaining } : {}),
          status,
          ...(minCostBasis ? { minCostBasis } : {}),
          ...(maxCostBasis ? { maxCostBasis } : {}),
          ...(minUnitCost ? { minUnitCost } : {}),
          ...(maxUnitCost ? { maxUnitCost } : {}),
          ...(sortBy ? { sortBy, sortDirection } : {}),
        },
        errorMessage: 'Failed to fetch lots',
      })
      if (response) {
        return response.data
      }
    },
    enabled: !!org?.id,
  })

  const rows = useMemo(
    () =>
      buildLotRows({
        items: data?.items ?? [],
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        openAsOf: openAsOf ? new Date(openAsOf) : new Date(),
      }),
    [data?.items, pagination.pageIndex, pagination.pageSize, openAsOf],
  )

  // The Table renders with table-layout: fixed, which ignores min/max-width
  // and collapses flexible columns to equal shares, so every column locks
  // size = minSize = maxSize and widths scale proportionally to fill the page.
  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'lotNumber',
          header: '#',
          size: 48,
          minSize: 48,
          maxSize: 48,
          cell: ({ row }) => (
            <span className="text-secondary">{row.original.lotNumber}</span>
          ),
        }),
        columnHelper.display({
          id: 'token',
          header: 'Token',
          size: 150,
          minSize: 150,
          maxSize: 150,
          cell: ({ row }) => (
            <TokenDisplay
              address={row.original.tokenAddress as IAddress}
              chainId={row.original.chainId as SupportedChainId}
              layout="two-line"
              size="small"
            />
          ),
          // Own stacking context so TokenDisplay's network badge can't paint
          // over the sticky header.
          meta: { cellClassName: 'isolate' },
        }),
        columnHelper.accessor('acquisitionTime', {
          id: 'acquisitionTime',
          header: 'Date received',
          size: 170,
          minSize: 170,
          maxSize: 170,
          enableSorting: true,
          cell: ({ row }) => (
            <div className="flex flex-col">
              <Timestamp
                timestamp={row.original.acquisitionTime}
                type="absolute"
                abbreviate
                showTooltip
                tz={tz}
              />
              <span className="whitespace-nowrap text-xs text-secondary">
                {formatWithLocalTimezone(
                  new Date(row.original.acquisitionTime),
                  { format: 'h:mm A', tz },
                )}{' '}
                · Held {row.original.heldDays}d
              </span>
            </div>
          ),
        }),
        columnHelper.display({
          id: 'from',
          header: 'From',
          size: 170,
          minSize: 170,
          maxSize: 170,
          cell: ({ row }) =>
            row.original.sourceAddress ? (
              // isInternal drops the badge icons and address chip so a name
              // gets the full cell width; the popover keeps the details.
              <div className="min-w-0 overflow-hidden">
                <AddressDisplay
                  address={row.original.sourceAddress as IAddress}
                  layout="standard"
                  size="small"
                  isInternal
                />
              </div>
            ) : (
              EMPTY_VALUE
            ),
        }),
        columnHelper.display({
          id: 'amount',
          header: 'Amount',
          size: 110,
          minSize: 110,
          maxSize: 110,
          cell: ({ row }) => (
            <span>
              {formatTokenAmount(row.original, row.original.quantity)}
            </span>
          ),
          meta: { align: 'right' },
        }),
        columnHelper.display({
          id: 'lotPrice',
          header: 'Lot price',
          size: 120,
          minSize: 120,
          maxSize: 120,
          cell: ({ row }) => <LotPriceCell row={row.original} />,
          meta: {
            align: 'right',
            headerTooltip: 'USD value of one token when the lot was opened',
          },
        }),
        columnHelper.accessor('costBasis', {
          id: 'costBasis',
          header: 'Cost basis',
          enableSorting: true,
          size: 130,
          minSize: 130,
          maxSize: 130,
          cell: ({ row }) => (
            <CurrencyAmount
              layout="standard"
              size="small"
              value={row.original.costBasis}
            />
          ),
          meta: { align: 'right' },
        }),
        columnHelper.display({
          id: 'remaining',
          header: 'Remaining',
          size: 250,
          minSize: 250,
          maxSize: 250,
          // Left-aligned so the bars share one anchor.
          cell: ({ row }) => <RemainingCell row={row.original} />,
        }),
        columnHelper.display({
          id: 'actions',
          header: '',
          ...TABLE_ACTION_COLUMN_WIDTH,
          cell: ({ row }) => <ActionsCell row={row.original} />,
          meta: TABLE_ACTION_COLUMN_META,
        }),
      ] as ColumnDef<LotRow>[],
    [tz],
  )

  const previousTotalCount = usePrevious(data?.totalCount)
  const totalCount =
    isLoading && !isNil(previousTotalCount)
      ? previousTotalCount
      : (data?.totalCount ?? 0)

  return (
    <>
      <LotRecomputeBanner recompute={recompute} />
      {data && !isLoading && (
        <LotTrustSignalsBanner
          signals={data.trustSignals}
          isRecomputing={recompute.isRunning && !recompute.hasPollingFailed}
          isStarting={recompute.isStarting}
          onRecompute={recompute.recompute}
          onViewDetails={() => {
            setGlobalModalState({
              isOpen: true,
              content: <LotTrustSignalsDialog signals={data.trustSignals} />,
            })
          }}
        />
      )}
      <Table
        size="small"
        columns={columns}
        data={rows}
        emptyMessage={
          trimmedSearch ? `No lots match "${trimmedSearch}"` : `No lots found`
        }
        onRowClick={(row) =>
          setGlobalModalState({
            isOpen: true,
            content: <LotDetailDialog lot={row.original} />,
          })
        }
        state={{ pagination, sorting }}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        showSorting
        pagination={{
          enabled: true,
          serverSide: true,
          totalCount,
          includeFirstAndLastPage: true,
          initialPageSize: INITIAL_PAGE_SIZE,
        }}
        showColumnFilters={false}
        isLoading={isLoading}
        skeletonCellClassName="h-[34px]"
        skeletonRowCount={pagination.pageSize}
      />
    </>
  )
}
